import React, { useState } from "react";
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { FileText, BookOpen, BookCopy, FileDown, CheckCircle2, Users, Play, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

// Custom components for the lecture
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative bg-gradient-to-r from-indigo-50 via-blue-50 to-slate-50 border border-indigo-200 rounded-lg p-6 my-6 overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-indigo-500 to-blue-600"></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full flex items-center justify-center">
            <CheckCircle2 className="h-3.5 w-3.5 text-white" />
          </div>
        </div>
        <div className="ml-3">
          <h4 className="text-base font-bold text-slate-900 mb-2 tracking-tight">Key Takeaway</h4>
          <p className="text-slate-700 leading-relaxed text-sm">{children}</p>
        </div>
      </div>
    </div>
  );
};

const ScientificDiagram = ({ 
  title, 
  caption, 
  children 
}: { 
  title: string; 
  caption: string; 
  children: React.ReactNode;
}) => {
  return (
    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg shadow-sm p-5 mb-6 overflow-hidden">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight">{title}</h3>
        <p className="text-xs text-slate-600 leading-relaxed">{caption}</p>
      </div>
      <div className="bg-white rounded-lg border border-slate-100 p-3">
        {children}
      </div>
    </div>
  );
};

const VideoPlaceholder = () => {
  return (
    <div className="w-full aspect-video bg-gray-100 rounded-lg flex items-center justify-center mb-6">
      <div className="text-center">
        <div className="w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <Play className="h-7 w-7 text-white" />
        </div>
        <p className="text-gray-600 font-medium text-sm">Lecture video will be available after the live session</p>
      </div>
    </div>
  );
};

// Slideshow component
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };
  
  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };
  
  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-base font-medium text-gray-900">Lecture Slides</h3>
        <div className="text-xs text-gray-500">
          Slide {currentSlide + 1} of {slides.length}
        </div>
      </div>
      
      <div className="p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{slides[currentSlide].title}</h2>
        <div className="mb-5 min-h-[400px] overflow-y-auto max-h-[500px] pr-1 thin-scrollbar">
          {slides[currentSlide].content}
        </div>
      </div>
      
      <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
        <button 
          onClick={goToPrevSlide}
          className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </button>
        
        <div className="flex space-x-1.5">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={index === currentSlide ? "h-2 w-2 rounded-full bg-blue-600" : "h-2 w-2 rounded-full bg-gray-300"}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <button 
          onClick={goToNextSlide}
          className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

const TestosteroneClinicalApplications: React.FC = () => {



  const lectureSlides = [
    {
      id: 1,
      title: "Introduction to Clinical Applications",
      content: (
        <>
          <div className="prose max-w-none">
            <p className="text-base leading-relaxed text-slate-700 mb-5 font-light">
              Optimizing testosterone levels holds significant clinical relevance for both men and women experiencing symptoms of deficiency. This lecture explores evidence-based approaches to testosterone optimization, including diagnostic considerations, treatment modalities, and comprehensive monitoring protocols.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div className="bg-gradient-to-br from-blue-50 via-blue-25 to-indigo-50 p-4 rounded-lg border border-blue-100 shadow-sm">
                <div className="flex items-center mb-2">
                  <div className="w-2.5 h-2.5 bg-blue-500 rounded-full mr-2"></div>
                  <h3 className="text-sm font-bold text-blue-900 tracking-tight">Clinical Significance</h3>
                </div>
                <p className="text-blue-800 leading-relaxed text-xs">
                  Testosterone deficiency affects an estimated 20-40% of men over age 45, with symptoms that significantly impact quality of life, metabolic health, and overall wellbeing. Modern optimization approaches focus on physiological restoration rather than supraphysiological enhancement.
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 via-emerald-25 to-green-50 p-4 rounded-lg border border-emerald-100 shadow-sm">
                <div className="flex items-center mb-2">
                  <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full mr-2"></div>
                  <h3 className="text-sm font-bold text-emerald-900 tracking-tight">Therapeutic Goals</h3>
                </div>
                <p className="text-emerald-800 leading-relaxed text-xs">
                  Effective testosterone optimization aims to restore physiological levels that support optimal energy, cognitive function, sexual health, body composition, and metabolic efficiency while minimizing potential risks through careful monitoring.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 rounded-lg border border-slate-200 mb-5">
              <h3 className="text-sm font-bold text-slate-900 mb-3 tracking-tight">Learning Objectives</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-slate-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-slate-700 text-xs">Understand diagnostic criteria and laboratory assessment</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-slate-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-slate-700 text-xs">Master treatment modalities and delivery systems</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-slate-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-slate-700 text-xs">Implement comprehensive monitoring protocols</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-slate-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-slate-700 text-xs">Manage complications and side effects</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-slate-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-slate-700 text-xs">Apply personalized treatment approaches</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-slate-600 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-slate-700 text-xs">Integrate lifestyle optimization strategies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Clinical testosterone optimization represents a powerful intervention for addressing multiple age-related conditions simultaneously. Success requires comprehensive assessment, individualized treatment selection, and ongoing monitoring to balance therapeutic benefits with safety considerations.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 2,
      title: "Diagnostic Criteria and Evaluation",
      content: (
        <>
          <ScientificDiagram 
            title="Comprehensive Testosterone Assessment" 
            caption="Key diagnostic criteria and laboratory evaluations for testosterone deficiency assessment."
          >
            <div className="flex justify-center py-3">
              <svg viewBox="0 0 800 450" className="w-full" style={{ maxHeight: '350px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="450" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#333333">Testosterone Assessment Framework</text>
                
                {/* Primary Assessment Box */}
                <g transform="translate(200, 175)">
                  {/* Background box */}
                  <rect x="-180" y="-70" width="360" height="220" rx="10" fill="#f1f8ff" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="-45" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">Laboratory Assessment</text>
                  
                  {/* Total T */}
                  <rect x="-160" y="-20" width="150" height="60" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-85" y="-5" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#3498DB">Total Testosterone</text>
                  <text x="-150" y="10" textAnchor="start" fontSize="9" fill="#3498DB">• Morning sample (7-10am)</text>
                  <text x="-150" y="25" textAnchor="start" fontSize="9" fill="#3498DB">• Repeat confirmation</text>
                  
                  {/* Free T */}
                  <rect x="10" y="-20" width="150" height="60" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="85" y="-5" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#3498DB">Free Testosterone</text>
                  <text x="20" y="10" textAnchor="start" fontSize="9" fill="#3498DB">• Calculated vs measured</text>
                  <text x="20" y="25" textAnchor="start" fontSize="9" fill="#3498DB">• Accounts for SHBG</text>
                  
                  {/* Other Tests */}
                  <rect x="-160" y="60" width="150" height="75" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="-85" y="75" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#3498DB">Additional Tests</text>
                  <text x="-150" y="90" textAnchor="start" fontSize="9" fill="#3498DB">• LH, FSH (etiology)</text>
                  <text x="-150" y="105" textAnchor="start" fontSize="9" fill="#3498DB">• Estradiol, SHBG</text>
                  <text x="-150" y="120" textAnchor="start" fontSize="9" fill="#3498DB">• Prolactin, CBC, PSA</text>
                  
                  {/* Safety Markers */}
                  <rect x="10" y="60" width="150" height="75" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="85" y="75" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#3498DB">Safety Markers</text>
                  <text x="20" y="90" textAnchor="start" fontSize="9" fill="#3498DB">• Hematocrit</text>
                  <text x="20" y="105" textAnchor="start" fontSize="9" fill="#3498DB">• PSA (men over 40)</text>
                  <text x="20" y="120" textAnchor="start" fontSize="9" fill="#3498DB">• Metabolic panel</text>
                </g>
                
                {/* Clinical Assessment Box */}
                <g transform="translate(600, 175)">
                  <rect x="-180" y="-70" width="360" height="220" rx="10" fill="#f8f1ff" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="-45" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Clinical Assessment</text>
                  
                  {/* Symptoms */}
                  <rect x="-160" y="-20" width="320" height="60" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#8E44AD">Key Symptoms</text>
                  <text x="-150" y="10" textAnchor="start" fontSize="9" fill="#8E44AD">• Fatigue & reduced energy</text>
                  <text x="-10" y="10" textAnchor="start" fontSize="9" fill="#8E44AD">• Sexual dysfunction</text>
                  <text x="120" y="10" textAnchor="start" fontSize="9" fill="#8E44AD">• Depression/mood issues</text>
                  <text x="-150" y="25" textAnchor="start" fontSize="9" fill="#8E44AD">• Reduced muscle mass</text>
                  <text x="-10" y="25" textAnchor="start" fontSize="9" fill="#8E44AD">• Body composition changes</text>
                  <text x="120" y="25" textAnchor="start" fontSize="9" fill="#8E44AD">• Cognitive changes</text>
                  
                  {/* Reversible factors */}
                  <rect x="-160" y="60" width="320" height="80" rx="5" fill="#E8DAEF" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="75" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#8E44AD">Potentially Reversible Factors</text>
                  <text x="-150" y="90" textAnchor="start" fontSize="9" fill="#8E44AD">• Obesity/metabolic syndrome</text>
                  <text x="20" y="90" textAnchor="start" fontSize="9" fill="#8E44AD">• Sleep apnea/sleep disorders</text>
                  <text x="-150" y="105" textAnchor="start" fontSize="9" fill="#8E44AD">• Stress/HPA axis dysfunction</text>
                  <text x="20" y="105" textAnchor="start" fontSize="9" fill="#8E44AD">• Medications (opioids, etc.)</text>
                  <text x="-150" y="120" textAnchor="start" fontSize="9" fill="#8E44AD">• Excessive alcohol consumption</text>
                  <text x="20" y="120" textAnchor="start" fontSize="9" fill="#8E44AD">• Zinc/vitamin D deficiencies</text>
                </g>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Laboratory Assessment</h3>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Total Testosterone:</strong> Morning sample, typically 7-10am when levels are highest</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Free Testosterone:</strong> Calculated or directly measured (more accurate)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>SHBG:</strong> Critical for understanding bioavailable hormone</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Estradiol:</strong> Essential to monitor for potential conversion issues</span>
                </li>
              </ul>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Clinical Evaluation</h3>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Symptoms:</strong> Must be present alongside lab values for diagnosis</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Validated Questionnaires:</strong> ADAM, qADAM, or AMS scales</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Rule Out Secondary Causes:</strong> Medication effects, lifestyle factors</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Screen for Contraindications:</strong> Prostate cancer, elevated Hct, etc.</span>
                </li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Comprehensive testosterone assessment requires both laboratory and clinical evaluation. Avoid treating numbers alone—the presence of consistent symptoms is essential for diagnosis. Remember that testosterone deficiency may be secondary to reversible factors that should be addressed first.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 3,
      title: "Treatment Modalities: Routes of Administration",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <h3 className="text-sm font-bold text-blue-900 mb-2">Injectable Testosterone</h3>
              <ul className="space-y-1.5 text-xs text-blue-800">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Forms:</strong> Cypionate, Enanthate, Propionate</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Dosing:</strong> 50-100mg weekly or 100-200mg biweekly</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Pros:</strong> Cost-effective, reliable absorption, flexible dosing</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Cons:</strong> Fluctuations in levels, injection requirement</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Peak:</strong> 24-48hrs post-injection</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <h3 className="text-sm font-bold text-green-900 mb-2">Transdermal Options</h3>
              <ul className="space-y-1.5 text-xs text-green-800">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Forms:</strong> Gels, creams, patches</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Dosing:</strong> Daily application (gels: 50-100mg)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Pros:</strong> Steady levels, ease of administration</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Cons:</strong> Transfer risk, variable absorption, cost</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Notes:</strong> Apply to clean, dry skin; wait to shower</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
              <h3 className="text-sm font-bold text-amber-900 mb-2">Other Formulations</h3>
              <ul className="space-y-1.5 text-xs text-amber-800">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Pellet Implants:</strong> 6-month duration, surgical insertion</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Nasal Gel:</strong> TID dosing, bypasses first-pass metabolism</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Buccal/Oral:</strong> Limited bioavailability, liver concerns</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Clomiphene:</strong> For secondary hypogonadism with preserved HPG axis</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>HCG:</strong> May preserve fertility during TRT</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-5 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Pharmacokinetic Comparison</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-2 px-3 text-left font-medium text-gray-700">Formulation</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-700">Half-life</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-700">Peak Levels</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-700">Stability</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-700">DHT Conversion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="py-2 px-3 text-gray-700">Cypionate</td>
                    <td className="py-2 px-3 text-gray-700">8 days</td>
                    <td className="py-2 px-3 text-gray-700">24-48 hrs</td>
                    <td className="py-2 px-3 text-gray-700">Fluctuating</td>
                    <td className="py-2 px-3 text-gray-700">Moderate</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 text-gray-700">Enanthate</td>
                    <td className="py-2 px-3 text-gray-700">7 days</td>
                    <td className="py-2 px-3 text-gray-700">24-48 hrs</td>
                    <td className="py-2 px-3 text-gray-700">Fluctuating</td>
                    <td className="py-2 px-3 text-gray-700">Moderate</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 text-gray-700">Gel/Cream</td>
                    <td className="py-2 px-3 text-gray-700">Daily dose</td>
                    <td className="py-2 px-3 text-gray-700">2-4 hrs</td>
                    <td className="py-2 px-3 text-gray-700">Stable</td>
                    <td className="py-2 px-3 text-gray-700">High (skin)</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3 text-gray-700">Pellets</td>
                    <td className="py-2 px-3 text-gray-700">3-6 months</td>
                    <td className="py-2 px-3 text-gray-700">Variable</td>
                    <td className="py-2 px-3 text-gray-700">Highly stable</td>
                    <td className="py-2 px-3 text-gray-700">Low</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <KeyTakeaway>
            The optimal testosterone delivery system should be tailored to patient preferences, lifestyle, and clinical response. Injectable testosterone offers the greatest flexibility in dosing and cost-effectiveness, while transdermal options provide more physiologic patterns but at higher cost. Regardless of delivery method, regular monitoring is essential to ensure optimal levels are maintained.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "Treatment Considerations: Dosing and Titration",
      content: (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Dosing Framework</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-2">Initial Dosing Parameters</h4>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>T. Cypionate/Enanthate:</strong> Start with 100-120mg/week (split into 2 doses)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>Transdermal Gel:</strong> 50mg daily, applied each morning</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>Pellets:</strong> 600-800mg initial dose (implanted)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>Clomiphene:</strong> 25mg every other day for secondary hypogonadism</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-xs font-medium text-gray-700 mb-2">Titration Approach</h4>
                <ul className="space-y-1 text-xs text-gray-600">
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>Labs:</strong> Check at trough (day before injection) at 6-8 weeks</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>Dosage Adjustment:</strong> Increments of 20-40mg/week based on labs and symptoms</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>Stabilization:</strong> Typically achieved within 3-6 months</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                    <span><strong>Monitoring Interval:</strong> Every 3-6 months once stable</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <ScientificDiagram 
            title="Injection Frequency & Stability Profile" 
            caption="Impact of injection frequency on serum testosterone levels. More frequent injections provide greater stability."
          >
            <div className="flex justify-center py-3">
              <svg viewBox="0 0 800 320" className="w-full" style={{ maxHeight: '250px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="320" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="30" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Testosterone Levels by Injection Frequency</text>
                
                {/* Legend */}
                <g transform="translate(550, 70)">
                  <rect x="0" y="0" width="200" height="70" rx="5" fill="#f8f9fa" stroke="#e9ecef" />
                  <text x="10" y="20" fontSize="11" fontWeight="bold" fill="#333">Legend</text>
                  
                  <line x1="10" y1="35" x2="30" y2="35" stroke="#3498db" strokeWidth="2" />
                  <text x="40" y="38" fontSize="9" fill="#333">Weekly Injection</text>
                  
                  <line x1="10" y1="55" x2="30" y2="55" stroke="#2ecc71" strokeWidth="2" />
                  <text x="40" y="58" fontSize="9" fill="#333">Twice Weekly</text>
                </g>
                
                {/* Axis */}
                <line x1="50" y1="250" x2="750" y2="250" stroke="#333" strokeWidth="1" />
                <line x1="50" y1="50" x2="50" y2="250" stroke="#333" strokeWidth="1" />
                
                {/* X-Axis labels (Days) */}
                <text x="400" y="280" textAnchor="middle" fontSize="11" fill="#333">Days</text>
                <text x="50" y="265" textAnchor="middle" fontSize="9" fill="#333">0</text>
                <text x="190" y="265" textAnchor="middle" fontSize="9" fill="#333">5</text>
                <text x="330" y="265" textAnchor="middle" fontSize="9" fill="#333">10</text>
                <text x="470" y="265" textAnchor="middle" fontSize="9" fill="#333">15</text>
                <text x="610" y="265" textAnchor="middle" fontSize="9" fill="#333">20</text>
                <text x="750" y="265" textAnchor="middle" fontSize="9" fill="#333">25</text>
                
                {/* Y-Axis labels (Testosterone levels) */}
                <text x="25" y="150" textAnchor="middle" fontSize="11" fill="#333" transform="rotate(-90, 25, 150)">Testosterone Level (ng/dL)</text>
                <text x="40" y="250" textAnchor="end" fontSize="9" fill="#333">0</text>
                <text x="40" y="210" textAnchor="end" fontSize="9" fill="#333">300</text>
                <text x="40" y="170" textAnchor="end" fontSize="9" fill="#333">600</text>
                <text x="40" y="130" textAnchor="end" fontSize="9" fill="#333">900</text>
                <text x="40" y="90" textAnchor="end" fontSize="9" fill="#333">1200</text>
                <text x="40" y="50" textAnchor="end" fontSize="9" fill="#333">1500</text>
                
                {/* Reference Ranges */}
                <rect x="50" y="170" width="700" height="40" fill="rgba(144, 238, 144, 0.2)" />
                <line x1="50" y1="170" x2="750" y2="170" stroke="#32CD32" strokeDasharray="3,2" />
                <line x1="50" y1="210" x2="750" y2="210" stroke="#32CD32" strokeDasharray="3,2" />
                <text x="755" y="190" textAnchor="start" fontSize="8" fill="#32CD32">Target Range</text>
                
                {/* Once Weekly Injection Line */}
                <path d="M50,90 Q95,130 140,170 Q186,210 235,210 Q280,210 330,170 Q375,130 420,90 Q465,130 510,170 Q555,210 605,210 Q650,210 700,170 Q745,130 750,90" fill="none" stroke="#3498db" strokeWidth="2" />
                
                {/* Twice Weekly Injection Line */}
                <path d="M50,130 Q73,150 95,170 Q118,190 140,190 Q163,190 186,170 Q208,150 235,130 Q257,150 280,170 Q303,190 325,190 Q348,190 370,170 Q393,150 420,130 Q442,150 465,170 Q488,190 510,190 Q533,190 555,170 Q578,150 605,130 Q627,150 650,170 Q673,190 695,190 Q718,190 740,170 Q763,150 750,130" fill="none" stroke="#2ecc71" strokeWidth="2" />
                
                {/* Injection markers - once weekly */}
                <circle cx="50" cy="90" r="4" fill="#3498db" />
                <circle cx="420" cy="90" r="4" fill="#3498db" />
                <text x="50" y="80" textAnchor="middle" fontSize="8" fill="#3498db">Injection</text>
                <text x="420" y="80" textAnchor="middle" fontSize="8" fill="#3498db">Injection</text>
                
                {/* Injection markers - twice weekly */}
                <circle cx="50" cy="130" r="3" fill="#2ecc71" />
                <circle cx="235" cy="130" r="3" fill="#2ecc71" />
                <circle cx="420" cy="130" r="3" fill="#2ecc71" />
                <circle cx="605" cy="130" r="3" fill="#2ecc71" />
                <text x="50" y="120" textAnchor="middle" fontSize="8" fill="#2ecc71">Inj.</text>
                <text x="235" y="120" textAnchor="middle" fontSize="8" fill="#2ecc71">Inj.</text>
                <text x="420" y="120" textAnchor="middle" fontSize="8" fill="#2ecc71">Inj.</text>
                <text x="605" y="120" textAnchor="middle" fontSize="8" fill="#2ecc71">Inj.</text>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
            <h3 className="text-xs font-bold text-blue-800 mb-2">Clinical Pearls: Optimizing Treatment Outcomes</h3>
            <ul className="space-y-1 text-xs text-blue-700">
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Frequency Matters:</strong> Twice-weekly injections provide more physiologic levels compared to once-weekly</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Individual Response:</strong> Genetic variations in metabolism and SHBG levels affect optimal dosing</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Adjust by Symptoms and Labs:</strong> Lab values inform but don't dictate treatment—clinical response matters</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Microdosing Approach:</strong> Smaller, more frequent doses often improve patient experience and minimize side effects</span>
              </li>
            </ul>
          </div>
          
          <KeyTakeaway>
            Successful testosterone optimization requires individualized dosing strategies based on pharmacokinetics, patient-specific factors, and clinical response. The trend toward more frequent, smaller doses better mimics natural hormone patterns and may reduce side effects while improving clinical outcomes.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 5,
      title: "Monitoring and Safety Considerations",
      content: (
        <>
          <ScientificDiagram 
            title="Comprehensive Monitoring Protocol" 
            caption="Systematic approach to monitoring patients on testosterone therapy."
          >
            <div className="flex justify-center py-2">
              <svg viewBox="0 0 800 350" className="w-full" style={{ maxHeight: '280px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="350" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="30" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Testosterone Therapy Monitoring Timeline</text>
                
                {/* Central timeline */}
                <line x1="80" y1="170" x2="720" y2="170" stroke="#777" strokeWidth="2" />
                
                {/* Timeline points */}
                <circle cx="80" cy="170" r="8" fill="#3498db" />
                <text x="80" y="145" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498db">Baseline</text>
                
                <circle cx="240" cy="170" r="8" fill="#3498db" />
                <text x="240" y="145" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498db">6-8 Weeks</text>
                
                <circle cx="400" cy="170" r="8" fill="#3498db" />
                <text x="400" y="145" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498db">3 Months</text>
                
                <circle cx="560" cy="170" r="8" fill="#3498db" />
                <text x="560" y="145" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498db">6 Months</text>
                
                <circle cx="720" cy="170" r="8" fill="#3498db" />
                <text x="720" y="145" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498db">Annual</text>
                
                {/* Monitoring elements */}
                <g transform="translate(80, 190)">
                  <rect x="-75" y="0" width="150" height="120" rx="5" fill="#e8f4f8" stroke="#3498db" strokeWidth="1" />
                  <text x="0" y="15" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">Baseline Assessment</text>
                  <text x="-65" y="35" textAnchor="start" fontSize="8" fill="#333">• Total & Free Testosterone</text>
                  <text x="-65" y="50" textAnchor="start" fontSize="8" fill="#333">• SHBG, Estradiol, LH, FSH</text>
                  <text x="-65" y="65" textAnchor="start" fontSize="8" fill="#333">• CBC with Hematocrit</text>
                  <text x="-65" y="80" textAnchor="start" fontSize="8" fill="#333">• Comprehensive Metabolic Panel</text>
                  <text x="-65" y="95" textAnchor="start" fontSize="8" fill="#333">• Lipid Panel</text>
                  <text x="-65" y="110" textAnchor="start" fontSize="8" fill="#333">• PSA (men over 40)</text>
                </g>
                
                <g transform="translate(240, 190)">
                  <rect x="-75" y="0" width="150" height="120" rx="5" fill="#e8f4f8" stroke="#3498db" strokeWidth="1" />
                  <text x="0" y="15" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">Initial Follow-up</text>
                  <text x="-65" y="35" textAnchor="start" fontSize="8" fill="#333">• Total & Free Testosterone</text>
                  <text x="-65" y="50" textAnchor="start" fontSize="8" fill="#333">• Estradiol</text>
                  <text x="-65" y="65" textAnchor="start" fontSize="8" fill="#333">• CBC with Hematocrit</text>
                  <text x="-65" y="80" textAnchor="start" fontSize="8" fill="#333">• Symptom assessment</text>
                  <text x="-65" y="95" textAnchor="start" fontSize="8" fill="#333">• Side effect review</text>
                  <text x="-65" y="110" textAnchor="start" fontSize="8" fill="#333">• Dosage adjustment if needed</text>
                </g>
                
                <g transform="translate(400, 190)">
                  <rect x="-75" y="0" width="150" height="120" rx="5" fill="#e8f4f8" stroke="#3498db" strokeWidth="1" />
                  <text x="0" y="15" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">3-Month Assessment</text>
                  <text x="-65" y="35" textAnchor="start" fontSize="8" fill="#333">• Total & Free Testosterone</text>
                  <text x="-65" y="50" textAnchor="start" fontSize="8" fill="#333">• Estradiol</text>
                  <text x="-65" y="65" textAnchor="start" fontSize="8" fill="#333">• CBC with Hematocrit</text>
                  <text x="-65" y="80" textAnchor="start" fontSize="8" fill="#333">• Comprehensive Metabolic Panel</text>
                  <text x="-65" y="95" textAnchor="start" fontSize="8" fill="#333">• Clinical response evaluation</text>
                  <text x="-65" y="110" textAnchor="start" fontSize="8" fill="#333">• Final dose adjustment</text>
                </g>
                
                <g transform="translate(560, 190)">
                  <rect x="-75" y="0" width="150" height="120" rx="5" fill="#e8f4f8" stroke="#3498db" strokeWidth="1" />
                  <text x="0" y="15" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">6-Month Follow-up</text>
                  <text x="-65" y="35" textAnchor="start" fontSize="8" fill="#333">• Total & Free Testosterone</text>
                  <text x="-65" y="50" textAnchor="start" fontSize="8" fill="#333">• Estradiol</text>
                  <text x="-65" y="65" textAnchor="start" fontSize="8" fill="#333">• CBC with Hematocrit</text>
                  <text x="-65" y="80" textAnchor="start" fontSize="8" fill="#333">• Lipid Panel</text>
                  <text x="-65" y="95" textAnchor="start" fontSize="8" fill="#333">• PSA (if applicable)</text>
                  <text x="-65" y="110" textAnchor="start" fontSize="8" fill="#333">• Check for stable response</text>
                </g>
                
                <g transform="translate(720, 190)">
                  <rect x="-75" y="0" width="150" height="120" rx="5" fill="#e8f4f8" stroke="#3498db" strokeWidth="1" />
                  <text x="0" y="15" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#333">Annual Monitoring</text>
                  <text x="-65" y="35" textAnchor="start" fontSize="8" fill="#333">• Complete hormone panel</text>
                  <text x="-65" y="50" textAnchor="start" fontSize="8" fill="#333">• CBC with Hematocrit</text>
                  <text x="-65" y="65" textAnchor="start" fontSize="8" fill="#333">• Comprehensive Metabolic Panel</text>
                  <text x="-65" y="80" textAnchor="start" fontSize="8" fill="#333">• Lipid Panel</text>
                  <text x="-65" y="95" textAnchor="start" fontSize="8" fill="#333">• PSA & DRE (age appropriate)</text>
                  <text x="-65" y="110" textAnchor="start" fontSize="8" fill="#333">• Reassess current regimen</text>
                </g>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h3 className="text-xs font-bold text-red-800 mb-2">Potential Adverse Effects</h3>
              <ul className="space-y-1 text-xs text-red-800">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Erythrocytosis:</strong> Most common (&gt;20% of patients), monitor hematocrit</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Estrogen-Related:</strong> Gynecomastia, fluid retention, mood changes</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Reproductive:</strong> Testicular atrophy, reduced spermatogenesis</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Prostate:</strong> Potential growth of subclinical prostate cancer</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Cardiovascular:</strong> Controversial—evidence mixed on CV risk</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h3 className="text-xs font-bold text-green-800 mb-2">Management Strategies</h3>
              <ul className="space-y-1 text-xs text-green-800">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Erythrocytosis:</strong> Therapeutic phlebotomy, dose reduction, frequency increase</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Estrogen Issues:</strong> Consider aromatase inhibitors if E2 &gt;60 pg/mL</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Fertility Concerns:</strong> Add HCG 500-1000 IU 2-3x weekly</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Prostate Monitoring:</strong> Annual PSA, DRE as age-appropriate</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>CV Risk:</strong> Monitor lipids, BP; consider CIMT, CAC for high-risk</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 mb-4">
            <h3 className="text-xs font-bold text-amber-800 mb-2">Essential Safety Thresholds</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-amber-100">
                    <th className="px-2 py-1.5 text-left font-medium text-amber-900">Parameter</th>
                    <th className="px-2 py-1.5 text-left font-medium text-amber-900">Concern Threshold</th>
                    <th className="px-2 py-1.5 text-left font-medium text-amber-900">Action Required</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-200">
                  <tr>
                    <td className="px-2 py-1.5 text-amber-800">Hematocrit</td>
                    <td className="px-2 py-1.5 text-amber-800">&gt;54%</td>
                    <td className="px-2 py-1.5 text-amber-800">Phlebotomy, dose reduction, increase hydration</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 text-amber-800">PSA</td>
                    <td className="px-2 py-1.5 text-amber-800">Velocity &gt;0.35 ng/mL/year</td>
                    <td className="px-2 py-1.5 text-amber-800">Urology referral, consider therapy pause</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 text-amber-800">Estradiol</td>
                    <td className="px-2 py-1.5 text-amber-800">&gt;60 pg/mL</td>
                    <td className="px-2 py-1.5 text-amber-800">Consider AI (anastrozole 0.5mg 1-2x weekly)</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 text-amber-800">Testosterone</td>
                    <td className="px-2 py-1.5 text-amber-800">&gt;1200 ng/dL</td>
                    <td className="px-2 py-1.5 text-amber-800">Dose reduction</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <KeyTakeaway>
            Systematic monitoring is essential for safe and effective testosterone therapy. A structured approach should include baseline assessment, regular follow-up at defined intervals, and adjustment of monitoring frequency based on individual response and risk factors. The greatest safety concerns—erythrocytosis, estrogen elevation, and prostate issues—can be effectively managed with proper surveillance and intervention.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "Special Clinical Considerations",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-800 mb-2">Testosterone in Women</h3>
              <p className="text-xs text-gray-600 mb-3">Testosterone plays vital roles in women's health, though at much lower physiologic levels than men (15-70 ng/dL vs. 400-1000 ng/dL).</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Indications:</strong> HSDD, sexual dysfunction, post-oophorectomy</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Dosing:</strong> Much lower (compounded cream 0.5-2mg daily, or 10% of male dose)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Monitoring:</strong> Virilization signs, voice changes, acne, lipid profile</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Target Levels:</strong> Upper physiologic female range or slightly above</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-800 mb-2">Metabolic Syndrome & Obesity</h3>
              <p className="text-xs text-gray-600 mb-3">Bidirectional relationship exists between testosterone and metabolic health. Low T promotes insulin resistance while metabolic syndrome suppresses testosterone production.</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Approach:</strong> Concurrent lifestyle intervention is essential</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Benefits:</strong> Improved insulin sensitivity, reduced visceral fat</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Synergy:</strong> TRT + metformin or GLP-1 RA often superior to either alone</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Caution:</strong> Higher aromatization in adipose tissue</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-800 mb-2">Cardiovascular Considerations</h3>
              <p className="text-xs text-gray-600 mb-3">The relationship between testosterone and cardiovascular health remains complex and somewhat controversial.</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>FDA Warning:</strong> 2015 warning about potential increased CV risk</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Recent Evidence:</strong> Most RCTs show neutral or beneficial effects when properly dosed</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Risk Stratification:</strong> Assess baseline CV risk; consider CAC scoring</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Monitoring:</strong> Blood pressure, lipids, hematocrit, inflammatory markers</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-xs font-semibold text-gray-800 mb-2">Fertility Considerations</h3>
              <p className="text-xs text-gray-600 mb-3">Exogenous testosterone suppresses the hypothalamic-pituitary-gonadal axis, reducing sperm production.</p>
              <ul className="space-y-1 text-xs text-gray-600">
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Prevention:</strong> HCG 500-1500 IU 2-3x weekly maintains testicular function</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Alternative:</strong> Clomiphene (25mg EOD) for secondary hypogonadism</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Recovery:</strong> Typically 3-6 months after discontinuation</span>
                </li>
                <li className="flex items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 mr-2 flex-shrink-0"></div>
                  <span><strong>Banking:</strong> Consider sperm cryopreservation before TRT initiation</span>
                </li>
              </ul>
            </div>
          </div>
          
          <ScientificDiagram 
            title="Estrogen Management in Testosterone Therapy" 
            caption="Balancing testosterone to estrogen ratio for optimal clinical outcomes."
          >
            <div className="flex justify-center py-2">
              <svg viewBox="0 0 800 300" className="w-full" style={{ maxHeight: '220px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="300" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="30" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Estrogen Balance in Testosterone Therapy</text>
                
                {/* Central graph area */}
                <rect x="100" y="70" width="600" height="180" fill="#f8f9fa" stroke="#e9ecef" strokeWidth="1" rx="5" />
                
                {/* X-Axis (Estradiol level) */}
                <line x1="100" y1="250" x2="700" y2="250" stroke="#333" strokeWidth="1" />
                <text x="400" y="275" textAnchor="middle" fontSize="10" fill="#333">Estradiol Level (pg/mL)</text>
                <text x="100" y="265" textAnchor="middle" fontSize="8" fill="#333">0</text>
                <text x="200" y="265" textAnchor="middle" fontSize="8" fill="#333">10</text>
                <text x="300" y="265" textAnchor="middle" fontSize="8" fill="#333">20</text>
                <text x="400" y="265" textAnchor="middle" fontSize="8" fill="#333">30</text>
                <text x="500" y="265" textAnchor="middle" fontSize="8" fill="#333">40</text>
                <text x="600" y="265" textAnchor="middle" fontSize="8" fill="#333">50</text>
                <text x="700" y="265" textAnchor="middle" fontSize="8" fill="#333">60+</text>
                
                {/* Estradiol zones */}
                <rect x="100" y="70" width="150" height="180" fill="rgba(255, 200, 200, 0.3)" />
                <text x="175" y="90" textAnchor="middle" fontSize="9" fill="#d32f2f">Too Low</text>
                <text x="175" y="105" textAnchor="middle" fontSize="8" fill="#d32f2f">Joint pain, mood issues</text>
                <text x="175" y="120" textAnchor="middle" fontSize="8" fill="#d32f2f">CVD risk, bone loss</text>
                
                <rect x="250" y="70" width="300" height="180" fill="rgba(200, 255, 200, 0.3)" />
                <text x="400" y="90" textAnchor="middle" fontSize="9" fill="#2e7d32">Optimal Range</text>
                <text x="400" y="105" textAnchor="middle" fontSize="8" fill="#2e7d32">20-40 pg/mL (men)</text>
                <text x="400" y="120" textAnchor="middle" fontSize="8" fill="#2e7d32">Balance of benefits/risks</text>
                
                <rect x="550" y="70" width="150" height="180" fill="rgba(255, 200, 200, 0.3)" />
                <text x="625" y="90" textAnchor="middle" fontSize="9" fill="#d32f2f">Too High</text>
                <text x="625" y="105" textAnchor="middle" fontSize="8" fill="#d32f2f">Gynecomastia, water retention</text>
                <text x="625" y="120" textAnchor="middle" fontSize="8" fill="#d32f2f">Emotional lability</text>
                
                {/* Management Strategies */}
                <g transform="translate(175, 200)">
                  <rect x="-60" y="-20" width="120" height="40" rx="5" fill="#fff" stroke="#d32f2f" strokeWidth="1" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#d32f2f">Management</text>
                  <text x="0" y="10" textAnchor="middle" fontSize="7" fill="#d32f2f">Reduce/eliminate AI</text>
                </g>
                
                <g transform="translate(400, 200)">
                  <rect x="-60" y="-20" width="120" height="40" rx="5" fill="#fff" stroke="#2e7d32" strokeWidth="1" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#2e7d32">Management</text>
                  <text x="0" y="10" textAnchor="middle" fontSize="7" fill="#2e7d32">Monitor and maintain</text>
                </g>
                
                <g transform="translate(625, 200)">
                  <rect x="-60" y="-20" width="120" height="40" rx="5" fill="#fff" stroke="#d32f2f" strokeWidth="1" />
                  <text x="0" y="-5" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#d32f2f">Management</text>
                  <text x="0" y="10" textAnchor="middle" fontSize="7" fill="#d32f2f">Consider AI (0.5mg 1-2x/week)</text>
                </g>
              </svg>
            </div>
          </ScientificDiagram>
          
          <KeyTakeaway>
            Special clinical scenarios require thoughtful protocol modifications. Women need much lower doses and careful monitoring for virilization. Metabolic syndrome patients often benefit greatly but require attention to estrogen management. Fertility preservation requires either HCG adjunct therapy or alternative approaches. The estrogen balance is critical—too low can be as problematic as too high.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 7,
      title: "Integrative Approaches & Optimization",
      content: (
        <>
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 p-4 mb-5">
            <h3 className="text-xs font-semibold text-gray-800 mb-3">Comprehensive Optimization Framework</h3>
            <p className="text-xs text-gray-600 mb-3">Optimizing testosterone therapy outcomes requires addressing multiple interconnected factors beyond hormone replacement alone.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                <h4 className="text-xs font-medium text-blue-800 mb-1.5">Lifestyle Foundations</h4>
                <ul className="space-y-1 text-xs text-blue-700">
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-blue-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Sleep:</strong> 7-9 hours quality sleep maximizes endogenous production</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-blue-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Exercise:</strong> Resistance training enhances receptor sensitivity</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-blue-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Stress:</strong> Cortisol management prevents testosterone suppression</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-blue-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Diet:</strong> Adequate healthy fats, zinc, magnesium, vitamin D</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                <h4 className="text-xs font-medium text-green-800 mb-1.5">Adjunctive Therapies</h4>
                <ul className="space-y-1 text-xs text-green-700">
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-green-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Zinc:</strong> 30-50mg daily improves testosterone utilization</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-green-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Vitamin D:</strong> Optimization to 50-80 ng/mL enhances outcomes</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-green-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Ashwagandha:</strong> May reduce SHBG, improve free T ratio</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-green-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Boron:</strong> 6-10mg daily may improve free:total T ratio</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                <h4 className="text-xs font-medium text-purple-800 mb-1.5">Advanced Considerations</h4>
                <ul className="space-y-1 text-xs text-purple-700">
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-purple-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Thyroid:</strong> Optimization improves testosterone response</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-purple-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>HCG:</strong> 250-500 IU 2-3x weekly prevents testicular atrophy</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-purple-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>Pregnenolone:</strong> 10-50mg may support neurosteroid balance</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1 h-1 rounded-full bg-purple-600 mt-1.5 mr-1.5 flex-shrink-0"></div>
                    <span><strong>DHEA:</strong> 25mg for women may enhance androgen sufficiency</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <ScientificDiagram 
            title="Testosterone Optimization Matrix" 
            caption="Interconnected factors that influence testosterone therapy outcomes."
          >
            <div className="flex justify-center py-2">
              <svg viewBox="0 0 800 300" className="w-full" style={{ maxHeight: '220px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="300" fill="#ffffff" />
                
                {/* Central circle - Testosterone */}
                <circle cx="400" cy="150" r="60" fill="#e8f4fa" stroke="#3498db" strokeWidth="2" />
                <text x="400" y="150" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498db">Testosterone</text>
                <text x="400" y="168" textAnchor="middle" fontSize="9" fill="#3498db">Optimization</text>
                
                {/* Surrounding factors */}
                <g transform="translate(400, 150)">
                  {/* Sleep */}
                  <g transform="translate(0, -120)">
                    <circle cx="0" cy="0" r="35" fill="#f0fdf4" stroke="#22c55e" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#22c55e">Sleep</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="7" fill="#22c55e">7-9 hours quality</text>
                    <line x1="0" y1="35" x2="0" y2="60" stroke="#22c55e" strokeWidth="1.5" strokeDasharray="3,2" />
                  </g>
                  
                  {/* Stress Management */}
                  <g transform="translate(115, -75)">
                    <circle cx="0" cy="0" r="35" fill="#fdf2f8" stroke="#ec4899" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#ec4899">Stress</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="7" fill="#ec4899">Management</text>
                    <line x1="-25" y1="25" x2="-55" y2="55" stroke="#ec4899" strokeWidth="1.5" strokeDasharray="3,2" />
                  </g>
                  
                  {/* Exercise */}
                  <g transform="translate(115, 75)">
                    <circle cx="0" cy="0" r="35" fill="#eff6ff" stroke="#3b82f6" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3b82f6">Exercise</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="7" fill="#3b82f6">Resistance training</text>
                    <line x1="-25" y1="-25" x2="-55" y2="-55" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="3,2" />
                  </g>
                  
                  {/* Nutrition */}
                  <g transform="translate(0, 120)">
                    <circle cx="0" cy="0" r="35" fill="#fff7ed" stroke="#f97316" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#f97316">Nutrition</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="7" fill="#f97316">Protein, fats, micronutrients</text>
                    <line x1="0" y1="-35" x2="0" y2="-60" stroke="#f97316" strokeWidth="1.5" strokeDasharray="3,2" />
                  </g>
                  
                  {/* Micronutrients */}
                  <g transform="translate(-115, 75)">
                    <circle cx="0" cy="0" r="35" fill="#faf5ff" stroke="#a855f7" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#a855f7">Micronutrients</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="7" fill="#a855f7">Zinc, Mg, Vit D</text>
                    <line x1="25" y1="-25" x2="55" y2="-55" stroke="#a855f7" strokeWidth="1.5" strokeDasharray="3,2" />
                  </g>
                  
                  {/* Thyroid & Metabolic */}
                  <g transform="translate(-115, -75)">
                    <circle cx="0" cy="0" r="35" fill="#ecfdf5" stroke="#10b981" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#10b981">Metabolic</text>
                    <text x="0" y="15" textAnchor="middle" fontSize="7" fill="#10b981">Thyroid, insulin sensitivity</text>
                    <line x1="25" y1="25" x2="55" y2="55" stroke="#10b981" strokeWidth="1.5" strokeDasharray="3,2" />
                  </g>
                </g>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 mb-4">
            <h3 className="text-xs font-bold text-indigo-800 mb-2">Clinical Pearls: Advanced Optimization</h3>
            <ul className="space-y-1 text-xs text-indigo-700">
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Individualization is Key:</strong> Template protocols should be customized based on individual response, genetics, and goals.</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Address Root Causes:</strong> Optimizing testosterone often unveils underlying metabolic issues that require parallel intervention.</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Beyond Numbers:</strong> Clinical response should guide adjustments beyond laboratory values alone.</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mt-1.5 mr-2 flex-shrink-0"></div>
                <span><strong>Synergistic Approach:</strong> Combining testosterone optimization with metabolic enhancement (GLP-1 RAs, metformin) can yield superior results in appropriate patients.</span>
              </li>
            </ul>
          </div>
          
          <KeyTakeaway>
            Optimizing testosterone therapy outcomes extends far beyond selecting the right dose and delivery method. A comprehensive approach addressing lifestyle foundations, nutritional status, stress management, and hormonal cross-talk will yield superior clinical results. Remember that testosterone replacement is most effective when viewed as part of a broader metabolic optimization strategy rather than an isolated intervention.
          </KeyTakeaway>
        </>
      )
    }
  ];

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container mx-auto px-4 py-6 flex-grow">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Testosterone: Clinical Applications</h1>
              <p className="text-sm text-gray-600">Practical protocols for diagnosis, treatment, and monitoring of testosterone optimization therapy</p>
            </div>
            
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">Overview</h2>
              <p className="text-sm text-gray-600 mb-3">A comprehensive guide to clinical testosterone optimization for both men and women, focusing on practical protocols, monitoring strategies, and integrative approaches.</p>
              
              <Separator className="my-4" />
            </div>
            
            <Slideshow slides={lectureSlides} />
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default TestosteroneClinicalApplications;